import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import countries from "i18n-iso-countries/index.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountryCombobox } from "@/components/ui/country-combobox";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { throwIfNotOk, apiSend } from "@/lib/api-error";
import { flagEmoji } from "@/lib/flag-emoji";

interface MultiplierRow {
  countryCode: string;
  multiplier: number;
  updatedAt: number;
  updatedBy: string | null;
}

const MIN_MULTIPLIER = 0.01;
const MAX_MULTIPLIER = 100;

function parseMultiplier(s: string): number | null {
  const n = Number(s.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  if (n < MIN_MULTIPLIER || n > MAX_MULTIPLIER) return null;
  return Math.round(n * 10000) / 10000;
}

export function CountryMultipliersSection() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "bs" ? "bs" : "en";
  const queryClient = useQueryClient();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["country-multipliers"],
    queryFn: async () => {
      const res = await fetch("/api/country-multipliers");
      await throwIfNotOk(res, "Greška pri učitavanju");
      return (await res.json()) as MultiplierRow[];
    },
  });

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newCode, setNewCode] = useState("");
  const [newMultiplier, setNewMultiplier] = useState("");

  const countryNames = useMemo(() => {
    const names = countries.getNames(locale, { select: "official" });
    return names as Record<string, string>;
  }, [locale]);

  const addedCodes = useMemo(() => rows.map((r) => r.countryCode), [rows]);

  const invalidateAfterMultiplierChange = () => {
    queryClient.invalidateQueries({ queryKey: ["country-multipliers"] });
    queryClient.invalidateQueries({ queryKey: ["services"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "services"] });
    queryClient.invalidateQueries({ queryKey: ["therapist", "services"] });
  };

  const upsertMutation = useMutation({
    mutationFn: (input: { countryCode: string; multiplier: number }) =>
      apiSend(
        `/api/country-multipliers/${input.countryCode}`,
        "PUT",
        { multiplier: input.multiplier },
        "Greška pri spremanju",
      ),
    onSuccess: invalidateAfterMultiplierChange,
  });

  const deleteMutation = useMutation({
    mutationFn: (countryCode: string) =>
      apiSend(`/api/country-multipliers/${countryCode}`, "DELETE", undefined, "Greška pri brisanju"),
    onSuccess: invalidateAfterMultiplierChange,
  });

  const handleSaveRow = (countryCode: string) => {
    const draft = drafts[countryCode];
    if (draft === undefined) return;
    const n = parseMultiplier(draft);
    if (n === null) return;
    upsertMutation.mutate(
      { countryCode, multiplier: n },
      {
        onSuccess: () =>
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[countryCode];
            return next;
          }),
      },
    );
  };

  const handleAddNew = () => {
    if (!newCode) return;
    const n = parseMultiplier(newMultiplier);
    if (n === null) return;
    upsertMutation.mutate(
      { countryCode: newCode, multiplier: n },
      {
        onSuccess: () => {
          setNewCode("");
          setNewMultiplier("");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label>Zemlja</Label>
            <CountryCombobox
              value={newCode || null}
              onChange={(c) => setNewCode(c ?? "")}
              placeholder="— odaberi —"
              excludeCodes={addedCodes}
            />
          </div>
          <div className="sm:w-32">
            <Label>Multiplikator</Label>
            <Input
              inputMode="decimal"
              value={newMultiplier}
              onChange={(e) => setNewMultiplier(e.target.value.replace(/[^0-9.,]/g, ""))}
              placeholder="npr. 2"
            />
          </div>
          <Button
            type="button"
            onClick={handleAddNew}
            disabled={!newCode || parseMultiplier(newMultiplier) === null || upsertMutation.isPending}
          >
            <Plus className="size-4" /> Dodaj
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Dozvoljeno: {MIN_MULTIPLIER} – {MAX_MULTIPLIER}, do 4 decimale.
        </p>
      </div>

      <div className="rounded-xl border border-border/60 bg-card shadow-sm">
        {isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Učitavanje...</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Još nema podešenih multiplikatora.
          </div>
        ) : (
          <ul className="divide-y divide-border/50">
            {rows.map((row) => {
              const draft = drafts[row.countryCode];
              const currentDisplay = draft ?? String(row.multiplier);
              const parsed = parseMultiplier(currentDisplay);
              const isDirty = draft !== undefined && parsed !== null && parsed !== row.multiplier;
              const name = countryNames[row.countryCode] ?? row.countryCode;
              return (
                <li key={row.countryCode} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="text-lg">{flagEmoji(row.countryCode)}</span>
                    <span className="flex-1 text-sm text-foreground">{name}</span>
                    <span className="text-xs text-muted-foreground">{row.countryCode}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      className="w-28"
                      inputMode="decimal"
                      value={currentDisplay}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [row.countryCode]: e.target.value.replace(/[^0-9.,]/g, ""),
                        }))
                      }
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveRow(row.countryCode)}
                      disabled={!isDirty || upsertMutation.isPending}
                      aria-label={`Sačuvaj ${name}`}
                    >
                      <FloppyDisk className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Ukloniti multiplikator za ${name}?`)) {
                          deleteMutation.mutate(row.countryCode);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      aria-label={`Ukloni ${name}`}
                    >
                      <Trash className="size-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
